import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Button,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Avatar,
  Paper,
  Popper,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Clear,
  FilterList,
  Favorite,
  FavoriteBorder,
  Store,
  Sort,
  ArrowUpward,
  ArrowDownward,
  TrendingDown,
  AttachMoney,
  AccessTime,
  SortByAlpha,
  Navigation,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import { url } from '../constants';

// ✅ Helper to fix the missing image path and link it to the backend server
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const DealsList = () => {
  const [categories, setCategories] = useState([]);
  const [deals, setDeals] = useState([]);
  const [sortedDeals, setSortedDeals] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = localStorage.getItem('selectedDealSearch');
    if (saved) {
      localStorage.removeItem('selectedDealSearch');
      return saved;
    }
    return '';
  });
  
  const [localSearchQuery, setLocalSearchQuery] = useState(() => {
    const saved = localStorage.getItem('selectedDealSearch');
    if (saved) {
      return saved;
    }
    return '';
  });
  
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const savedCategoryId = localStorage.getItem('selectedDealCategoryId');
    const savedCategoryName = localStorage.getItem('selectedDealCategory');
    
    if (savedCategoryId && savedCategoryName) {
      localStorage.removeItem('selectedDealCategoryId');
      localStorage.removeItem('selectedDealCategory');
      return [{ id: parseInt(savedCategoryId), name: savedCategoryName }];
    }
    return [];
  });
  
  const [sortBy, setSortBy] = useState('percentage_discount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [anchorEl, setAnchorEl] = useState(null);

  const { userLocation } = useLocationContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sortOptions = [
    { value: 'percentage_discount', label: 'Discount %', icon: <TrendingDown /> },
    { value: 'discounted_price', label: 'Price', icon: <AttachMoney /> },
    { value: 'distance', label: 'Distance', icon: <Navigation /> },
    { value: 'created_at', label: 'Newest', icon: <AccessTime /> },
    { value: 'deal_name', label: 'Name', icon: <SortByAlpha /> },
  ];

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  useEffect(() => {
    if (localSearchQuery.length < 2) {
      setSearchSuggestions([]);
      return;
    }
    
    const debounceTime = isMobile ? 700 : 300;
    const timer = setTimeout(() => {
      fetchSearchSuggestions(localSearchQuery);
    }, debounceTime);
    
    return () => clearTimeout(timer);
  }, [localSearchQuery, isMobile]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // ✅ FIXED: Reset everything when filters change
  useEffect(() => {
    console.log('🔄 Filters changed, resetting...');
    setPage(0);
    setDeals([]);
    setSortedDeals([]); // <- Clear sorted deals too
    setHasMore(true);
    fetchDeals(0, true);
  }, [selectedCategories, searchQuery, userLocation]);

  useEffect(() => {
    sortDealsLocally();
  }, [deals, sortBy, sortOrder]);

  // ✅ FIXED: Cleanup and recreate observer when deals change
  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!loadMoreRef.current || loadingMore || !hasMore || loading || sortedDeals.length === 0) {
      return;
    }

    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          console.log('✅ Loading more deals...');
          loadMoreDeals();
        }
      });
    };

    observerRef.current = new IntersectionObserver(callback, options);
    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, sortedDeals.length]); // <- Added sortedDeals.length

  const fetchSearchSuggestions = async (query) => {
    if (window.dealsSearchAbortController) {
      window.dealsSearchAbortController.abort();
    }
    
    window.dealsSearchAbortController = new AbortController();
    setSearchLoading(true);
    
    try {
      const timeoutId = setTimeout(() => {
        if (window.dealsSearchAbortController) {
          window.dealsSearchAbortController.abort();
        }
      }, isMobile ? 5000 : 10000);
      
      const response = await fetch(
        `${url}/deals/search-suggestions?query=${encodeURIComponent(query)}&limit=4`,
        { signal: window.dealsSearchAbortController.signal }
      );
      
      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (data.success) {
        setSearchSuggestions(data.suggestions);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('❌ Error fetching search suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${url}/users/get_categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
      console.log('✅ Categories loaded:', data);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
    }
  };

  const fetchDeals = async (pageNum = page, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    setError('');
    
    try {
      const skip = pageNum * 20;
      let endpoint = `${url}/deals/all?limit=20&skip=${skip}`;

      if (selectedCategories.length > 0) {
        const categoryIds = selectedCategories.map(cat => cat.id).join(',');
        endpoint += `&category_ids=${categoryIds}`;
        console.log(`📂 Filtering by category IDs: ${categoryIds}`);
      }

      if (searchQuery) {
        endpoint += `&search=${encodeURIComponent(searchQuery)}`;
        console.log(`🔍 Filtering by search: ${searchQuery}`);
      }

      console.log('🔍 Fetching deals:', endpoint, 'Page:', pageNum);

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch deals');

      let data = await response.json();

      if (userLocation && userLocation.lat && userLocation.lng) {
        data = data.map(deal => {
          if (deal.location && deal.location.coordinates) {
            const [dealLng, dealLat] = deal.location.coordinates;
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              dealLat,
              dealLng
            );
            return {
              ...deal,
              distance: parseFloat(distance.toFixed(1))
            };
          }
          return { ...deal, distance: null };
        });
      }

      console.log(`✅ Fetched ${data.length} deals (page ${pageNum}, skip ${skip})`);

      if (data.length < 20) {
        console.log('🛑 No more deals to load');
        setHasMore(false);
      }

      if (reset) {
        setDeals(data);
      } else {
        setDeals(prev => {
          const newDeals = [...prev, ...data];
          console.log('📊 Total deals now:', newDeals.length);
          return newDeals;
        });
      }
    } catch (error) {
      console.error('❌ Error fetching deals:', error);
      setError(error.message || 'Failed to load deals');
      if (reset) setDeals([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreDeals = useCallback(() => {
    if (loadingMore || !hasMore || loading) {
      console.warn('⚠️ Cannot load more');
      return;
    }
    
    const nextPage = page + 1;
    console.log('📄 Loading next page:', nextPage);
    setPage(nextPage);
    fetchDeals(nextPage, false);
  }, [page, loadingMore, hasMore, loading]);

  const sortDealsLocally = () => {
    if (deals.length === 0) {
      setSortedDeals([]);
      return;
    }

    let sorted = [...deals];

    switch (sortBy) {
      case 'percentage_discount':
        sorted.sort((a, b) => {
          const valA = parseFloat(a.percentage_discount) || 0;
          const valB = parseFloat(b.percentage_discount) || 0;
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        });
        break;

      case 'discounted_price':
        sorted.sort((a, b) => {
          const valA = parseFloat(a.discounted_price) || 0;
          const valB = parseFloat(b.discounted_price) || 0;
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        });
        break;

      case 'distance':
        sorted.sort((a, b) => {
          const distA = a.distance !== null ? a.distance : Infinity;
          const distB = b.distance !== null ? b.distance : Infinity;
          return sortOrder === 'asc' ? distA - distB : distB - distA;
        });
        break;

      case 'created_at':
        sorted.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;

      case 'deal_name':
        sorted.sort((a, b) => {
          const nameA = (a.deal_name || '').toLowerCase();
          const nameB = (b.deal_name || '').toLowerCase();
          return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        break;

      default:
        break;
    }

    setSortedDeals(sorted);
  };

  const handleToggleFavorite = async (dealId, e) => {
    e.stopPropagation();
    
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    const isFav = favorites.includes(dealId);
    setFavoritesLoading(prev => ({ ...prev, [dealId]: true }));
    
    let updatedFavorites;
    if (isFav) {
      updatedFavorites = favorites.filter(fav => fav !== dealId);
    } else {
      updatedFavorites = [...favorites, dealId];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem('userFavorites', JSON.stringify(updatedFavorites));

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isFav 
        ? `${url}/deals/remove-favorite` 
        : `${url}/deals/add-favorite`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deal_id: dealId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update favorites');
      }

      setSnackbar({
        open: true,
        message: isFav ? 'Removed from favorites' : 'Added to favorites',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      setFavorites(favorites);
      localStorage.setItem('userFavorites', JSON.stringify(favorites));
      
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update favorites',
        severity: 'error'
      });
    } finally {
      setFavoritesLoading(prev => {
        const newState = { ...prev };
        delete newState[dealId];
        return newState;
      });
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategories(prev => {
      const exists = prev.find(cat => cat.id === category.cate_id);
      
      if (exists) {
        const updated = prev.filter(cat => cat.id !== category.cate_id);
        console.log('➖ Removed category ID:', category.cate_id);
        return updated;
      } else {
        const updated = [...prev, { id: category.cate_id, name: category.cate_name }];
        console.log('➕ Added category ID:', category.cate_id, category.cate_name);
        return updated;
      }
    });
  };

  const handleRemoveCategory = (categoryId) => {
    setSelectedCategories(prev => {
      const updated = prev.filter(cat => cat.id !== categoryId);
      console.log('🗑️ Removed category ID:', categoryId);
      return updated;
    });
  };

  // ✅ FIXED: Clear all and reset states
  const handleClearAllFilters = () => {
    console.log('🧹 Clearing all filters');
    setSelectedCategories([]);
    setLocalSearchQuery('');
    setSearchQuery('');
    setSortBy('percentage_discount');
    setSortOrder('desc');
    setPage(0); // <- Reset page
    setHasMore(true); // <- Reset hasMore
    localStorage.removeItem('selectedDealCategory');
    localStorage.removeItem('selectedDealSearch');
  };

  const handleSearchSubmit = () => {
    console.log('🔍 Submitting search:', localSearchQuery);
    setSearchQuery(localSearchQuery);
    setSearchSuggestions([]);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    console.log('📂 Selected suggestion:', suggestion.deal_name);
    window.open(`/deal/${suggestion.id}`, '_blank');
  };

  const handleSortMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'distance' ? 'asc' : 'desc');
    }
    handleSortMenuClose();
  };

  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const isFavorite = (dealId) => favorites.includes(dealId);

  const getSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    return option ? option.label : 'Sort';
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Get category color from saved categories list
  const getCategoryColor = (categoryName) => {
    const savedCategory = categories.find(cat => 
      cat.cate_name === categoryName || 
      categoryName.includes(cat.cate_name) ||
      cat.cate_name.includes(categoryName)
    );
    
    if (savedCategory && savedCategory.cate_color) {
      return savedCategory.cate_color;
    }

    const colorMap = {
      'Electronics': '#FF6B6B',
      'Fashion': '#4ECDC4',
      'Food': '#45B7D1',
      'Grocery': '#96CEB4',
      'Travel': '#FECA57',
      'Home': '#FF9FF3',
      'Beauty': '#54A0FF',
      'Books': '#5F27CD',
      'Sports': '#00D2D3',
      'Groceries': '#1a7f3a',
      'Food & Dining': '#FF6B6B',
      'Shopping & Retail': '#4ECDC4',
    };
    
    return colorMap[categoryName] || '#1a7f3a';
  };

  // Much darker shade for category chip (40% darker, solid fill)
  const getDarkCategoryChipColor = (categoryName) => {
    const color = getCategoryColor(categoryName);
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkerR = Math.max(0, Math.floor(r * 0.6));
    const darkerG = Math.max(0, Math.floor(g * 0.6));
    const darkerB = Math.max(0, Math.floor(b * 0.6));
    
    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  };

  const renderSearchOption = (props, option) => (
    <Box
      {...props}
      sx={{
        display: 'flex',
        gap: isMobile ? 1 : 2,
        alignItems: 'center',
        p: isMobile ? 1.5 : 1.5,
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        '&:hover': { bgcolor: '#f9f9f9' },
        '&:active': { bgcolor: '#f0f0f0' },
      }}
    >
      {!isMobile && (option.deal_url || option.image_url || option.img_url) && (
        <Avatar
          src={getImageUrl(option.deal_url || option.image_url || option.img_url)}
          alt={option.deal_name}
          sx={{ width: 40, height: 40 }}
        />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="body2" 
          fontWeight={600} 
          sx={{ fontSize: isMobile ? '0.85rem' : '0.9rem' }}
        >
          {option.deal_name}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        >
          {option.vendor_name} • {option.category}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ color: '#4B5563', mb: 1 }}>
            All Deals
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
            Showing deals from all locations
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: isMobile ? 2 : 4, px: isMobile ? 2 : 0 }}>
          <Autocomplete
            freeSolo
            fullWidth
            options={searchSuggestions}
            getOptionLabel={(option) => 
              typeof option === 'string' ? option : option.deal_name
            }
            loading={searchLoading}
            inputValue={localSearchQuery}
            onInputChange={(event, newValue) => {
              setLocalSearchQuery(newValue);
            }}
            onChange={(event, value) => {
              if (value && typeof value === 'object') {
                handleSuggestionClick(value);
              }
            }}
            filterOptions={(x) => x}
            PopperComponent={(props) => (
              <Popper 
                {...props} 
                style={{ 
                  width: props.anchorEl ? props.anchorEl.clientWidth : '100%'
                }} 
                placement="bottom-start"
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 4],
                    },
                  },
                ]}
              />
            )}
            PaperComponent={({ children }) => (
              <Paper 
                elevation={8}
                sx={{ 
                  maxHeight: isMobile ? '250px' : '400px',
                  overflow: 'auto'
                }}
              >
                {children}
              </Paper>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedCategories.length > 0
                    ? `Search in ${selectedCategories.map(c => c.name).join(', ')}...`
                    : 'Search for deals, products, or brands...'
                }
                onKeyPress={handleSearchKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Search sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {searchLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <CircularProgress color="inherit" size={20} />
                        </Box>
                      )}
                      <InputAdornment position="end">
                        {localSearchQuery && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLocalSearchQuery('');
                              setSearchQuery('');
                              setSearchSuggestions([]);
                            }}
                          >
                            <Clear />
                          </IconButton>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSearchSubmit}
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            ml: 1,
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { bgcolor: '#0f5328' },
                          }}
                        >
                          Search
                        </Button>
                      </InputAdornment>
                    </>
                  ),
                }}
              />
            )}
            renderOption={renderSearchOption}
            noOptionsText={localSearchQuery.length < 2 ? "Type to search..." : "No deals found"}
          />
        </Box>

        {/* Sort Controls */}
        <Box sx={{ mb: isMobile ? 2 : 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', px: isMobile ? 2 : 0 }}>
          <Button
            variant="outlined"
            startIcon={<Sort />}
            size={isMobile ? 'small' : 'medium'}
            onClick={handleSortMenuOpen}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: `${theme.palette.primary.main}15`,
              },
            }}
          >
            Sort by: {getSortLabel()}
          </Button>

          <IconButton
            onClick={handleToggleSortOrder}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
              '&:hover': {
                bgcolor: `${theme.palette.primary.main}15`,
              },
            }}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>

          <Typography variant="caption" color="text.secondary">
            {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </Typography>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSortMenuClose}
          PaperProps={{
            sx: { minWidth: 220 },
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              selected={sortBy === option.value}
              disabled={option.value === 'distance' && !userLocation}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText>
                {option.label}
                {option.value === 'distance' && !userLocation && ' (Set location first)'}
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>

        {/* Active Filters */}
        {(selectedCategories.length > 0 || searchQuery) && (
          <Box sx={{ mb: isMobile ? 2 : 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', px: isMobile ? 2 : 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
              Active Filters:
            </Typography>
            {selectedCategories.map((category) => (
              <Chip
                key={category.id}
                label={category.name}
                onDelete={() => handleRemoveCategory(category.id)}
                sx={{ 
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                    '&:hover': {
                      color: '#e0e0e0',
                    },
                  },
                }}
              />
            ))}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                onDelete={() => {
                  setSearchQuery('');
                  setLocalSearchQuery('');
                }}
                sx={{
                  bgcolor: '#FF9F43',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                  },
                }}
              />
            )}
            <Button
              size="small"
              onClick={handleClearAllFilters}
              startIcon={<Clear />}
              sx={{ 
                textTransform: 'none',
                ml: 1,
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            >
              Clear All
            </Button>
          </Box>
        )}

        {/* Categories Filter */}
        <Box sx={{ mb: isMobile ? 2 : 4, px: isMobile ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterList sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              Categories
            </Typography>
            {selectedCategories.length > 0 && (
              <Chip
                label={`${selectedCategories.length} selected`}
                size="small"
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {categories.map((category) => {
              const isSelected = selectedCategories.some(cat => cat.id === category.cate_id);
              return (
                <Chip
                  key={category.cate_id}
                  label={category.cate_name}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    bgcolor: isSelected ? theme.palette.primary.main : '#f0f0f0',
                    color: isSelected ? 'white' : '#4B5563',
                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                    '&:hover': {
                      bgcolor: isSelected ? theme.palette.primary.main : '#e0e0e0',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          {loading
            ? 'Loading deals...'
            : `Found ${sortedDeals.length} deal${sortedDeals.length !== 1 ? 's' : ''}`}
          {selectedCategories.length > 0 && !loading && (
            <span> in {selectedCategories.map(c => c.name).join(', ')}</span>
          )}
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#1a7f3a' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: isMobile ? 2 : 0 }}>
            {error}
          </Alert>
        )}

        {/* ✅ FIXED: Card grid identically updated to use deal_url with object-fit cover */}
        {!loading && sortedDeals.length > 0 && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: isMobile ? 0 : 2,
                mb: 4,
              }}
            >
              {sortedDeals.map((deal, index) => {
                const dealId = deal._id || deal.id || `deal-${index}`;
                const isLoadingFav = favoritesLoading[dealId];
                const darkChipColor = getDarkCategoryChipColor(deal.category);

                // Attempt to grab deal_url, fallback to image_url, or show placeholder
                const rawImageUrl = deal.deal_url || deal.image_url || deal.img_url;
                const finalImageUrl = rawImageUrl 
                  ? getImageUrl(rawImageUrl) 
                  : 'https://via.placeholder.com/400x300?text=No+Image';

                return (
                  <Card
                    key={dealId}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: isMobile ? 0 : 1,
                      border: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderRight: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderBottom: isMobile ? '2px solid #E5E7EB' : 'none',
                      '&:hover': {
                        boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                        transform: isMobile ? 'none' : 'translateY(-4px)',
                      },
                    }}
                    onClick={() => window.open(`/deal/${dealId}`, '_blank')}
                  >
                    {/* ✅ Image Container with Object-Fit Cover */}
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height={isMobile ? "140" : "180"}
                        image={finalImageUrl}
                        alt={deal.deal_name}
                        sx={{
                          objectFit: 'cover',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      />

                        {deal.distance !== undefined && deal.distance !== null && (
                          <Chip
                            icon={<Navigation />}
                            label={`${deal.distance} km`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: isMobile ? 8 : 12,
                              left: isMobile ? 8 : 12,
                              fontWeight: 700,
                              bgcolor: '#1a7f3a', // Updated background color
                              color: 'white',     // Set text color to white for contrast
                              fontSize: isMobile ? '0.65rem' : '0.7rem',
                              height: isMobile ? 20 : 24,
                              backdropFilter: 'blur(10px)',
                              zIndex: 3,
                              '& .MuiChip-icon': {
                                color: 'white',   // Set icon color to white
                              },
                            }}
                          />
                        )}
                      
                      <Chip
                        label={`${deal.percentage_discount}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: isMobile ? 8 : 12,
                          right: isMobile ? 8 : 12,
                          fontWeight: 700,
                          bgcolor: '#FF4444',
                          color: 'white',
                          fontSize: isMobile ? '0.65rem' : '0.7rem',
                          height: isMobile ? 20 : 24,
                          boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                          zIndex: 3,
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 0.5 : 1 }}>
                        <Chip
                          label={deal.category}
                          size="small"
                          sx={{
                            bgcolor: darkChipColor,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            height: isMobile ? 18 : 24,
                            width: '100%',
                            justifyContent: 'center',
                          }}
                        />
                      </Stack>

                      {/* ✅ Deal Title moved into CardContent for readability */}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#212121',
                          fontSize: isMobile ? '0.8rem' : '0.95rem',
                        }}
                      >
                        {deal.deal_name}
                      </Typography>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Store sx={{ fontSize: 12, color: '#878787' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.vendor_name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ 
                            color: '#1a7f3a',
                            fontSize: isMobile ? '0.85rem' : '1rem',
                          }}
                        >
                          ₹{deal.discounted_price}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: 'line-through',
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                          }}
                        >
                          ₹{deal.original_price}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <LocationOn sx={{ fontSize: 18, color: '#1a7f3a' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.city}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          onClick={(e) => !isLoadingFav && handleToggleFavorite(dealId, e)}
                          sx={{ 
                            cursor: isLoadingFav ? 'default' : 'pointer',
                            transition: 'all 0.3s ease', 
                            '&:hover': { opacity: isLoadingFav ? 1 : 0.7 },
                            minWidth: '60px',
                          }}
                        >
                          {isLoadingFav ? (
                            <CircularProgress size={isMobile ? 16 : 20} sx={{ color: '#1a7f3a' }} />
                          ) : isFavorite(dealId) ? (
                            <Favorite fontSize="small" sx={{ color: '#FF6B6B', fontSize: isMobile ? 16 : 20 }} />
                          ) : (
                            <FavoriteBorder fontSize="small" sx={{ color: 'text.secondary', fontSize: isMobile ? 16 : 20 }} />
                          )}
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                          >
                            {isLoadingFav ? 'Saving...' : isFavorite(dealId) ? 'Saved' : 'Save'}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/deal/${dealId}`, '_blank');
                          }}
                          sx={{
                            borderColor: '#1a7f3a',
                            color: '#1a7f3a',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isMobile ? '0.65rem' : '0.8rem',
                            py: isMobile ? 0.4 : 0.75,
                            minHeight: isMobile ? 28 : 36,
                            px: isMobile ? 1 : 2,
                            '&:hover': {
                              borderColor: '#1a7f3a',
                              bgcolor: '#E8F5E9',
                            },
                          }}
                        >
                          View
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {/* Load More Trigger */}
            {hasMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  minHeight: '100px',
                  px: isMobile ? 2 : 0,
                }}
              >
                {loadingMore && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} sx={{ color: '#1a7f3a' }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more deals...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {!hasMore && sortedDeals.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 4, px: isMobile ? 2 : 0 }}>
                <Typography variant="body2" color="text.secondary">
                  🎉 You've reached the end! Total: {sortedDeals.length} deals
                </Typography>
              </Box>
            )}
          </>
        )}

        {!loading && sortedDeals.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8, px: isMobile ? 2 : 0 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No deals found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your filters or search query
            </Typography>
            <Button
              variant="contained"
              onClick={handleClearAllFilters}
              sx={{
                bgcolor: '#1a7f3a',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#0f5328' },
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DealsList;